import { AfterViewInit, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Country } from 'src/app/models/country';
import { Timezone } from 'src/app/models/timezone';
import { ApiService } from 'src/app/services/api.services';
import { GeoService } from 'src/app/services/geo.service';
import { NavigationHistoryService } from 'src/app/services/navigation-history.service';
import { NotAuthorizedNoticeComponent } from '../../../components/not-authorized-notice/not-authorized-notice.component';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	MatAutocompleteTrigger,
	MatAutocomplete,
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/select';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'app-add-property-details',
	templateUrl: './add-property-details.component.html',
	styleUrls: [
		'./add-property-details.component.scss',
		'../../common/main.scss',
		'../../common/stepper.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		HasSystemCapabilityDirective,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatAutocompleteTrigger,
		MatAutocomplete,
		MatOption,
		MatHint,
		MatButton,
		AsyncPipe,
	],
})
export class AddPropertyDetailsComponent implements AfterViewInit {
	@Input() editProperty!: EditProperty;
	@Input() propertyType!: PropertyType;

	filteredCountries!: Observable<Country[]>;
	filteredTimezones!: Observable<Timezone[]>;
	timezone!: Timezone[];

	longitudeLabel = '';
	latitudeLabel = '';

	latitudeGenFromAddress = $localize`:@@addPropertyDetails-LatitudeGenFromAddress:Latitude (or generate from address)`;
	localizeLatitude = $localize`:@@addPropertyDetails-Latitude:Latitude`;
	longitudeGenFromAddress = $localize`:@@addPropertyDetails-LongitudeGenFromAddress:Longitude (or generate from address)`;
	localizeLongitude = $localize`:@@addPropertyDetails-Longitude:Longitude`;

	readonly activeOptions = [
		{ value: true, label: 'Active' },
		{ value: false, label: 'Inactive' },
	];
	showCountryError = false;
	showTimezoneError = false;

	constructor(
		private readonly router: Router,
		private readonly geoService: GeoService,
		private readonly navHistory: NavigationHistoryService,
		private readonly apiService: ApiService
	) {
		this.longitudeLabel = this.longitudeGenFromAddress;
		this.latitudeLabel = this.latitudeGenFromAddress;
	}

	get isExistingProperty(): boolean {
		return this.propertyType === PropertyType.existingProperty;
	}

	async ngAfterViewInit(): Promise<void> {
		this.timezone = await this.apiService.getTimezone();
	}

	public get isValid(): boolean {
		switch (this.propertyType) {
			case PropertyType.newProperty:
				const requiredPropertyFields = [this.editProperty.name];
				const propertyComplete = requiredPropertyFields.reduce(
					(accumulator, value) => (value?.length ?? 0) > 0 && accumulator,
					true
				);

				const validCountryCode = this.validateCountryCode(
					this.editProperty.countryCode
				);

				if (!validCountryCode) {
					return false;
				}

				const requiredAddressFields = [
					this.editProperty.address1,
					this.editProperty.locality,
					this.editProperty.stateProvinceRegion,
					this.editProperty.postalCode,
					this.editProperty.countryCode,
				];

				const addressComplete = requiredAddressFields.reduce(
					(accumulator, value) => (value?.length ?? 0) > 0 && accumulator,
					true
				);

				const requiredCoordinatesFields = [
					this.editProperty.latitude,
					this.editProperty.longitude,
				];

				const coordinatesComplete = requiredCoordinatesFields.reduce(
					(accumulator, value) => (value?.length ?? 0) > 0 && accumulator,
					true
				);

				if (!propertyComplete) {
					return false;
				}

				if (propertyComplete && addressComplete) {
					return true;
				}

				if (propertyComplete && coordinatesComplete) {
					return this.geoCoordinatesValid(
						this.editProperty.latitude as string,
						this.editProperty.longitude as string
					);
				}

				return false;

			case PropertyType.existingProperty:
			default:
				return true;
		}
	}

	geoCoordinatesValid(latitude: string, longitude: string): boolean {
		// If there are no lat and long values return true
		if (latitude === '' || longitude === '') {
			return true;
		}

		// If there are lat and long values, check to see if they are in correct lat/long format
		const lat = parseInt(latitude, 10);
		const lng = parseInt(longitude, 10);

		if (isNaN(lat) || lat === undefined || isNaN(lng) || lng === undefined) {
			return false;
		}

		if (lat < -90 || lat > 90) {
			return false;
		} else if (lng < -180 || lng > 180) {
			return false;
		}
		return true;
	}

	incrementOnClick(): void {
		if (this.editProperty?.pendingInstalls != null) {
			if (this.editProperty.pendingInstalls >= 1000) {
				this.editProperty.pendingInstalls = 1000;
			} else {
				this.editProperty.pendingInstalls =
					this.editProperty.pendingInstalls + 1;
			}
		}
	}

	decrementOnClick(): void {
		if (this.editProperty?.pendingInstalls != null) {
			if (this.editProperty.pendingInstalls <= 0) {
				this.editProperty.pendingInstalls = 0;
			} else {
				this.editProperty.pendingInstalls =
					this.editProperty.pendingInstalls - 1;
			}
		}
	}

	filterCountries(value: any): void {
		this.filteredCountries = of(this._filter(value));
	}

	filterTimezones(value: any): void {
		this.filteredTimezones = of(this._filterTimezone(value));
	}

	private _filter(value: string): Country[] {
		const filterValue = value.toLowerCase();
		return this.geoService.allCountries.filter((country) =>
			country.name.toLowerCase().includes(filterValue)
		);
	}

	private _filterTimezone(value: string): Timezone[] {
		const filterValue = value.toLowerCase();
		return this.timezone.filter(
			(res) =>
				res.timezone.toLowerCase().includes(filterValue) ||
				res.description?.toLowerCase().includes(filterValue)
		);
	}

	openAutocompleteCountries() {
		this.filteredCountries = of(this.geoService.allCountries);
	}

	openAutocompleteTimezones() {
		this.filteredTimezones = of(this.timezone);
	}
	countrySelected() {
		this.showCountryError = false;
	}

	countryInput() {
		this.showCountryError = true;
	}

	timezoneSelected() {
		// show the error if user does not select a country from the list per the requirements
		this.showTimezoneError = false;
	}

	timezoneInput() {
		// show the error if user does not select a timezone from the list per the requirements
		this.showTimezoneError = true;
	}

	displayCountryName = (twoLetterCode: string): string => {
		let foundCountry = null;
		if (this.geoService?.allCountries) {
			foundCountry = this.geoService.allCountries.find(
				(country) => country.twoLetterCode === twoLetterCode
			);
		}

		return foundCountry && foundCountry.name ? foundCountry.name : '';
	};

	displayTimezoneName = (tz: string): string => {
		let foundTimezone = null;
		if (this.timezone) {
			foundTimezone = this.timezone.find((res) => res.timezone === tz);
		}

		return foundTimezone && foundTimezone.timezone
			? foundTimezone.timezone + ' - ' + foundTimezone.description
			: '';
	};

	validateCountryCode(country: string | undefined): boolean {
		if (!country) {
			return false;
		}

		if (country.length > 2) {
			return false;
		} else if (country.length < 2) {
			return false;
		} else {
			return true;
		}
	}

	setLongitudeLabel(): void {
		if (this.editProperty.longitude === '') {
			this.longitudeLabel = this.longitudeGenFromAddress;
		} else {
			this.longitudeLabel = this.localizeLongitude;
		}
	}

	setLatitudeLabel(): void {
		if (this.editProperty.latitude === '') {
			this.latitudeLabel = this.latitudeGenFromAddress;
		} else {
			this.latitudeLabel = this.localizeLatitude;
		}
	}
}

export enum PropertyType {
	newProperty,
	existingProperty,
}

export interface PropertyDetailsEvent {
	editProperty: EditProperty;
	propertyType: PropertyType;
}

export type EditProperty = {
	id?: string;
	companyId?: string;
	name?: string;
	pendingInstalls?: number;
	active?: boolean;
	address1?: string;
	address2?: string;
	locality?: string;
	countryCode?: string;
	stateProvinceRegion?: string;
	postalCode?: string;
	latitude?: string;
	longitude?: string;
	timezone?: string;
};

export const emptyEditProperty = (): EditProperty => ({
	pendingInstalls: 0,
	active: true,
});

export const isNumber = (value: any): boolean => /^[-]?\d+\.?\d*$/.test(value);
